import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, UploadCloud, CheckCircle, Download, Layout } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useResumeStore from "../../../store/useResumeStore";
import LivePreview from "../../../components/resume/builder/LivePreview";
import TemplateSelectionPopup from "../../../components/resume/builder/TemplateSelectionPopup";
import useTemplateStore from "@/store/useTemplateStore";
import Navigation from "../../../components/landing/Navigation";

const steps = [
    { id: "upload", label: "Upload Resume" },
    { id: "template", label: "Choose Template" },
    { id: "enhance", label: "AI Enhance" },
    { id: "preview", label: "Preview" },
    { id: "download", label: "Download" },
];

const ResumeEnhancer = () => {
    const [resumeText, setResumeText] = useState("");
    const [loading, setLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState("upload");
    const [selectedFile, setSelectedFile] = useState(null);
    const navigate = useNavigate();
    const apiUrl = import.meta.env.VITE_API_PORT;

    const { currentTemplate, loadTemplateForRoute, setCurrentTemplate } = useTemplateStore();

    const {
        updatePersonalInfo,
        addExperience,
        addEducation,
        addSkill,
        addProject,
        personalInfo,
        experience,
        education,
        skills,
        projects,
    } = useResumeStore();

    // Load template on mount - specific to enhancer route
    useEffect(() => {
        const initTemplate = async () => {
            await loadTemplateForRoute('enhancer');
        };
        initTemplate();
    }, []);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedFile(file);
        const formData = new FormData();
        formData.append("resume", file);
        setLoading(true);
        try {
            const res = await axios.post(`${apiUrl}/api/resume/extract-text`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResumeText(res.data.text);
        } catch {
            alert("Error extracting text. Is backend running?");
        } finally {
            setLoading(false);
        }
    };

    const handleEnhance = async () => {
        if (!resumeText.trim()) return;
        setLoading(true);
        try {
            const res = await axios.post(`${apiUrl}/api/resume/enhance-resume`, {
                resumeText,
            });

            const { personalInfo, experience, education, skills, projects } = res.data;
            Object.entries(personalInfo).forEach(([k, v]) => updatePersonalInfo(k, v));
            experience?.forEach(addExperience);
            education?.forEach(addEducation);
            skills?.forEach(addSkill);
            projects?.forEach(addProject);

            setCurrentStep("preview");
        } catch {
            alert("Error enhancing resume. Check backend/API key.");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        // Validate personal info first
        if (!personalInfo || !personalInfo.fullName) {
            alert("❌ Resume data is incomplete. Please ensure you have a name in your personal information.");
            return;
        }

        try {
            setLoading(true);
            
            // Prepare resume data - template is optional
            const resumeData = {
                personalInfo: {
                    fullName: personalInfo.fullName || 'Your Name',
                    email: personalInfo.email || '',
                    phone: personalInfo.phone || '',
                    location: personalInfo.location || '',
                    website: personalInfo.website || '',
                    summary: personalInfo.summary || ''
                },
                experience: Array.isArray(experience) ? experience : [],
                education: Array.isArray(education) ? education : [],
                skills: Array.isArray(skills) ? skills : [],
                projects: Array.isArray(projects) ? projects : [],
                template: currentTemplate || null // Send null if no template selected
            };

            console.log('📥 Initiating download with:', {
                name: resumeData.personalInfo.fullName,
                hasTemplate: !!currentTemplate,
                templateName: currentTemplate?.name || 'Default',
                experienceCount: resumeData.experience.length,
                educationCount: resumeData.education.length,
                skillsCount: resumeData.skills.length,
                projectsCount: resumeData.projects.length
            });

            const response = await axios.post(
                `${apiUrl}/api/resume/download`,
                resumeData,
                {
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    timeout: 30000, // 30 second timeout
                    validateStatus: function (status) {
                        return status >= 200 && status < 500; // Don't throw on 4xx errors
                    }
                }
            );

            // Check if response is an error
            if (response.status >= 400) {
                const text = await response.data.text();
                let errorMsg = 'Unknown error';
                try {
                    const errorData = JSON.parse(text);
                    errorMsg = errorData.error || errorData.details || errorMsg;
                } catch (e) {
                    errorMsg = text || errorMsg;
                }
                throw new Error(errorMsg);
            }

            // Check if response has data
            if (!response.data) {
                throw new Error('No data received from server');
            }

            if (response.data.size === 0) {
                throw new Error('Empty PDF received from server');
            }

            // Check content type
            const contentType = response.headers['content-type'] || response.headers['Content-Type'];
            if (contentType && !contentType.includes('application/pdf')) {
                // Server might have sent JSON error as blob
                const text = await response.data.text();
                try {
                    const error = JSON.parse(text);
                    throw new Error(error.error || error.details || 'Server returned non-PDF response');
                } catch (e) {
                    throw new Error('Received invalid response from server');
                }
            }

            console.log('✅ PDF received successfully:', {
                size: response.data.size,
                type: contentType
            });

            // Create blob and download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Create filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const sanitizedName = personalInfo.fullName.replace(/[^a-zA-Z0-9]/g, '-');
            const fileName = `${sanitizedName}-enhanced-${timestamp}.pdf`;
            link.download = fileName;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
            
            console.log('✅ Download completed:', fileName);
            
            // Save template preference if template was selected
            if (currentTemplate) {
                await setCurrentTemplate(currentTemplate, 'enhancer');
                console.log('✅ Template preference saved');
            }
            
            // Show success message
            alert(`✅ Resume downloaded successfully as ${fileName}`);
            
        } catch (error) {
            console.error('❌ Download failed:', error);
            
            // Provide detailed error message
            let errorMessage = '❌ Error downloading resume\n\n';
            
            if (error.code === 'ECONNABORTED') {
                errorMessage += 'Request timed out. The server might be slow or unresponsive.\n\nPlease try again.';
            } else if (error.code === 'ERR_NETWORK') {
                errorMessage += 'Network error. Please check:\n• Backend server is running\n• Correct API URL is configured\n• No firewall blocking the connection';
            } else if (error.response) {
                errorMessage += `Server responded with error (${error.response.status}).\n\n`;
                errorMessage += error.message || 'Please try again.';
            } else if (error.request) {
                errorMessage += 'No response from server.\n\nPlease check if the backend is running on ' + apiUrl;
            } else {
                errorMessage += error.message || 'An unexpected error occurred.';
            }
            
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Navigation />
            <div className="min-h-screen bg-black relative overflow-hidden mt-16">
                {/* Steps + Heading */}
                <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
                    <div className="flex justify-center mb-8">
                        <h1 className="flex items-center gap-4 text-6xl lg:text-7xl font-black tracking-tight">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#79e708] to-[#5bb406] rounded-2xl shadow-2xl shadow-[#79e708]/30">
                                <span className="text-3xl font-black text-black">AI</span>
                            </div>
                            <span className="bg-gradient-to-r from-white via-[#79e708] to-white bg-clip-text text-transparent">
                                Resume Enhancer
                            </span>
                        </h1>
                    </div>
                    <p className="text-center text-gray-400 mb-12">
                        Follow the steps below to upload, enhance, and preview your resume before continuing.
                    </p>

                    {/* Steps Indicator */}
                    <div className="flex justify-between items-center mb-12">
                        {steps.map((s, i) => {
                            const activeIndex = steps.findIndex((st) => st.id === currentStep);
                            const completed = i < activeIndex;
                            const active = s.id === currentStep;
                            return (
                                <div key={s.id} className="flex flex-col items-center flex-1">
                                    <div
                                        className={`w-12 h-12 flex items-center justify-center rounded-full border-2 transition-all ${
                                            completed
                                                ? "bg-[#79e708] border-[#79e708] text-black"
                                                : active
                                                ? "border-[#79e708] text-[#79e708]"
                                                : "border-gray-700 text-gray-500"
                                        }`}
                                    >
                                        {completed ? <CheckCircle className="w-6 h-6" /> : i + 1}
                                    </div>
                                    <span
                                        className={`mt-2 text-sm ${
                                            completed || active ? "text-white" : "text-gray-500"
                                        }`}
                                    >
                                        {s.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Step Content */}
                    <AnimatePresence mode="wait">
                        {/* Upload Step */}
                        {currentStep === "upload" && (
                            <motion.div
                                key="upload"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                className="text-center"
                            >
                                <label
                                    htmlFor="resumeUpload"
                                    className="cursor-pointer border-2 border-dashed border-[#79e708]/50 rounded-2xl px-6 py-10 flex flex-col items-center justify-center gap-4 text-center hover:bg-[#79e708]/5 transition-all"
                                >
                                    <UploadCloud className="w-10 h-10 text-[#79e708]" />
                                    <span className="text-white font-semibold text-lg">
                                        Click to upload your resume
                                    </span>
                                    <span className="text-sm text-gray-400">
                                        Accepted formats: <strong>PDF</strong>, <strong>DOCX</strong>
                                    </span>
                                    <Input
                                        id="resumeUpload"
                                        type="file"
                                        accept=".pdf,.docx"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </label>
                                {selectedFile && <p className="mt-4 text-white">{selectedFile.name}</p>}

                                {/* Continue to Template Selection */}
                                <Button
                                    onClick={() => setCurrentStep("template")}
                                    disabled={!resumeText || loading}
                                    className="mt-6 bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-2 rounded-full shadow transition-all"
                                >
                                    Continue to Template Selection
                                </Button>
                            </motion.div>
                        )}

                        {/* Template Selection Step */}
                        {currentStep === "template" && (
                            <motion.div
                                key="template"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                className="text-center"
                            >
                                <div className="mb-6">
                                    <Layout className="w-16 h-16 mx-auto text-[#79e708] mb-4" />
                                    <h2 className="text-2xl font-bold text-white mb-2">Choose Your Template</h2>
                                    <p className="text-gray-400">Select a template for your enhanced resume</p>
                                </div>

                                {/* Template Selection Component */}
                                <div className="bg-gray-900/50 rounded-2xl p-6 mb-6">
                                    <TemplateSelectionPopup 
                                        onClose={() => {}} 
                                        source="enhancer"
                                        inline={true}
                                    />
                                </div>

                                {currentTemplate && (
                                    <p className="text-sm text-gray-400 mb-6">
                                        Selected: <span className="text-[#79e708] font-semibold">{currentTemplate.name || 'Template'}</span>
                                    </p>
                                )}

                                <div className="flex gap-4 justify-center">
                                    <Button
                                        onClick={() => setCurrentStep("upload")}
                                        className="bg-gray-800 text-white font-semibold px-6 py-2 rounded-full shadow hover:bg-gray-700 transition-all"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setCurrentStep("enhance");
                                            handleEnhance();
                                        }}
                                        disabled={!currentTemplate || loading}
                                        className="bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-2 rounded-full shadow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enhancing...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-4 h-4 mr-2" /> Enhance with AI
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {!currentTemplate && (
                                    <p className="text-red-400 text-sm mt-4">
                                        ⚠️ Please select a template to continue
                                    </p>
                                )}
                            </motion.div>
                        )}

                        {currentStep === "enhance" && (
                            <motion.div
                                key="enhance"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                className="text-center"
                            >
                                <img src="/f8e57931-7a82-474f-980b-dc7951fcc4c7.gif" alt="" className="mx-auto" />
                                <p className="mt-6 text-gray-400">Enhancing your resume with AI...</p>
                            </motion.div>
                        )}

                        {currentStep === "preview" && (
                            <motion.div
                                key="preview"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                            >
                                <LivePreview />
                                <div className="flex justify-center mt-6 gap-4">
                                    <Button
                                        onClick={() => navigate("/ai-resume-builder/preview")}
                                        className="bg-white text-black font-semibold px-6 py-2 rounded-full shadow hover:bg-gray-200 transition-all"
                                    >
                                        Edit Resume
                                    </Button>
                                    <Button
                                        onClick={() => setCurrentStep("download")}
                                        className="bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-2 rounded-full shadow transition-all"
                                    >
                                        Continue
                                    </Button>
                                </div>
                            </motion.div>
                        )}

                        {currentStep === "download" && (
                            <motion.div
                                key="download"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -30 }}
                                className="text-center text-white"
                            >
                                <h2 className="text-2xl font-bold mb-4">
                                    🎉 Resume Process Completed!
                                </h2>
                                <p className="text-gray-400 mb-6">
                                    You're ready to move forward with your enhanced resume.
                                </p>

                                {currentTemplate && (
                                    <p className="text-sm text-gray-500 mb-4">
                                        Using template: <span className="text-[#79e708]">{currentTemplate.name || 'Selected'}</span>
                                    </p>
                                )}

                                <div className="flex gap-4 justify-center">
                                    <Button
                                        onClick={() => setCurrentStep("template")}
                                        className="bg-gray-800 text-white font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-gray-700 transition-all"
                                    >
                                        Change Template
                                    </Button>
                                    <Button
                                        onClick={handleDownload}
                                        disabled={loading || !currentTemplate}
                                        className="bg-gradient-to-r from-[#79e708] to-[#5bb406] hover:brightness-110 text-black font-semibold px-6 py-3 rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Preparing...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-5 h-5 mr-2" /> Download Resume
                                            </>
                                        )}
                                    </Button>
                                </div>

                                {!currentTemplate && (
                                    <p className="text-red-400 text-sm mt-4">
                                        ⚠️ Please select a template before downloading
                                    </p>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </>
    );
};

export default ResumeEnhancer;